<template>
  <div :class="containerClasses" :style="containerStyle">
    <img
      v-if="!hasError"
      :src="imageSrc"
      :srcset="imageSrcSet"
      :sizes="sizes"
      :alt="alt"
      :width="width"
      :height="height"
      :loading="loadingAttr"
      :fetchpriority="fetchPriorityAttr"
      :decoding="decodingAttr"
      :class="imageClasses"
      :style="imageStyle"
      @load="handleLoad"
      @error="handleError"
    />
    <div
      v-if="isLoading && !hasError"
      class="absolute inset-0 flex items-center justify-center bg-gray-100"
    >
      <div
        class="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-primary-500"
      ></div>
    </div>
    <div
      v-if="hasError"
      class="absolute inset-0 flex items-center justify-center bg-gray-100"
    >
      <div class="text-center text-gray-400">
        <i class="fas fa-image text-4xl"></i>
        <p class="mt-2 text-sm">
          {{ errorMessage || $t('error.imageLoadFailed') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useImageLoader } from '@/composables/useImageLoader';
import type { GlideFit } from '@/types/image';
import { IMAGE_CONFIG } from '@/config/image';

interface Props {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  sizes?: string;
  priority?: boolean;
  fill?: boolean;
  quality?: number;
  fit?: GlideFit;
  containerClass?: string;
  imageClass?: string;
  errorMessage?: string;
}

const props = withDefaults(defineProps<Props>(), {
  width: undefined,
  height: undefined,
  sizes: '100vw',
  priority: false,
  fill: false,
  quality: IMAGE_CONFIG.defaultQuality,
  fit: 'cover',
  containerClass: '',
  imageClass: '',
  errorMessage: '',
});

const {
  getOptimalWidths,
  generateSrcSet,
  buildImageUrl,
  getAspectRatio,
  isAbsoluteUrl,
} = useImageLoader();

const isLoading = ref(true);
const hasError = ref(false);

const aspectRatio = computed(() => getAspectRatio(props.width, props.height));

const loadingAttr = computed(() => (props.priority ? 'eager' : 'lazy'));
const fetchPriorityAttr = computed(() => (props.priority ? 'high' : undefined));
const decodingAttr = computed(() => (props.priority ? 'sync' : 'async'));

const imageSrc = computed(() => {
  if (isAbsoluteUrl(props.src)) {
    return props.src;
  }

  return buildImageUrl(
    props.src,
    props.width,
    props.height,
    props.quality,
    props.fit,
    IMAGE_CONFIG.defaultFormat,
  );
});

const imageSrcSet = computed(() => {
  if (isAbsoluteUrl(props.src)) {
    return undefined;
  }

  if (props.fill) {
    const widths = getOptimalWidths();
    return generateSrcSet(
      props.src,
      widths,
      aspectRatio.value,
      props.quality,
      props.fit,
      IMAGE_CONFIG.defaultFormat,
    );
  }

  if (props.width) {
    const widths = getOptimalWidths(props.width);
    return generateSrcSet(
      props.src,
      widths,
      aspectRatio.value,
      props.quality,
      props.fit,
      IMAGE_CONFIG.defaultFormat,
    );
  }

  return undefined;
});

const containerClasses = computed(() => {
  const classes = ['relative overflow-hidden'];

  if (props.fill) {
    classes.push('absolute inset-0');
  }

  if (props.containerClass) {
    classes.push(props.containerClass);
  }

  return classes.join(' ');
});

const containerStyle = computed(() => {
  if (!props.fill && aspectRatio.value) {
    return {
      aspectRatio: aspectRatio.value.toString(),
    };
  }
  return {};
});

const imageClasses = computed(() => {
  const classes = ['h-full w-full'];

  if (props.fit === 'cover') {
    classes.push('object-cover');
  } else if (props.fit === 'contain') {
    classes.push('object-contain');
  } else if (props.fit === 'fill') {
    classes.push('object-fill');
  }

  if (props.imageClass) {
    classes.push(props.imageClass);
  }

  return classes.join(' ');
});

const imageStyle = computed(() => {
  if (props.fill) {
    return {
      position: 'absolute',
      height: '100%',
      width: '100%',
      left: 0,
      top: 0,
      right: 0,
      bottom: 0,
    };
  }
  return {};
});

function handleLoad() {
  isLoading.value = false;
  hasError.value = false;
}

function handleError() {
  isLoading.value = false;
  hasError.value = true;
}
</script>
